import React, { useState } from "react";
import { Button, List, Card, Typography, InputNumber, message, Modal, Input, Select, Form } from "antd";
import {
  WalletOutlined,
  PlusCircleOutlined,
  ArrowLeftOutlined,
  QrcodeOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { backend_url } from "../LOCAL/VARIABLE";
import {qr} from "../LOCAL/VARIABLE"

const { Title, Text } = Typography;
const { Option } = Select;

// Dummy bank details
const BANK_DETAILS = {
  upi: {
    id: "9477712227@ptyes",
    name: "Vahan Solution",
  },
  bank: {
    name: "Kotak Mahindra",
    accountNumber: "0948546479",
    ifsc: "KKBK0006600",
    accountName: "Vahan Solution"
  }
};

const fetchBalance = async (userId) => {
  const res = await fetch(`${backend_url}/api/wallet/balance/${userId}`);
  return res.json();
};

const fetchHistory = async (userId) => {
  const res = await fetch(`${backend_url}/api/wallet/history/${userId}`);
  return res.json();
};

// Copy to clipboard function
const copyToClipboard = (text, successMessage) => {
  navigator.clipboard.writeText(text).then(() => {
    message.success(successMessage);
  }).catch(() => {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    message.success(successMessage);
  });
};

const Wallet = ({ user }) => {
  const [amount, setAmount] = useState(100);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [transactionId, setTransactionId] = useState("");
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { data: balance } = useQuery({
    queryKey: ["wallet-balance", user?._id],
    queryFn: () => fetchBalance(user?._id),
  });

  const { data: history = [] } = useQuery({
    queryKey: ["wallet-history", user?._id],
    queryFn: () => fetchHistory(user?._id),
  });

  const handleAddFunds = () => {
    setIsModalVisible(true);
  };

  const handlePaymentSubmit = () => {
    if (!transactionId.trim()) {
      message.error("Please enter Transaction ID");
      return;
    }

    const messageText = `Hey Admin, I have paid ₹${amount} via ${paymentMethod.toUpperCase()}. 
Transaction ID: ${transactionId}
User Email: ${user.email}
User ID: ${user._id}
Payment Method: ${paymentMethod}`;

    window.open(
      `https://wa.me/919477712227?text=${encodeURIComponent(messageText)}`,
      "_blank"
    );

    setIsModalVisible(false);
    setTransactionId("");
    form.resetFields();
    message.success("Payment details shared with admin via WhatsApp");
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setTransactionId("");
    form.resetFields();
  };

  // Copy handlers
  const copyUpiId = () => {
    copyToClipboard(BANK_DETAILS.upi.id, "UPI ID copied to clipboard!");
  };

  const copyBankDetails = () => {
    const bankDetails = `Bank: ${BANK_DETAILS.bank.name}
Account Number: ${BANK_DETAILS.bank.accountNumber}
IFSC: ${BANK_DETAILS.bank.ifsc}
Account Name: ${BANK_DETAILS.bank.accountName}`;
    
    copyToClipboard(bankDetails, "Bank details copied to clipboard!");
  };

  const copyAccountNumber = () => {
    copyToClipboard(BANK_DETAILS.bank.accountNumber, "Account number copied to clipboard!");
  };

  const copyIfsc = () => {
    copyToClipboard(BANK_DETAILS.bank.ifsc, "IFSC code copied to clipboard!");
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 p-1">
      {/* Header */}
      <header className="flex items-center bg-white shadow-md px-2 py-3 fixed top-0 left-0 w-full z-50 h-[55px]">
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 20, color: "red" }} />}
          onClick={() => navigate(-1)}
        />
        <Title level={5} className="m-0 flex-1 text-center text-red-600">
          Wallet
        </Title>
      </header>

      <div className="mt-[65px] space-y-4 flex-1 overflow-y-auto">
        {/* Balance Card */}
        <Card className="rounded-2xl shadow-md text-center bg-white">
          <WalletOutlined style={{ fontSize: 50, color: "#2563eb" }} />
          <Title level={3} className="mt-2">
            ₹{balance?.balance || 0}
          </Title>
          <Text type="secondary">Available Balance</Text>
        </Card>

        {/* Add Funds Card */}
        <Card className="rounded-2xl shadow-md bg-white p-4">
          <Title level={5}>Add Money</Title>
          <InputNumber
            min={50}
            value={amount}
            onChange={setAmount}
            className="w-full mb-2"
            size="large"
          />
          <Button
            type="primary"
            block
            icon={<PlusCircleOutlined />}
            onClick={handleAddFunds}
            size="large"
          >
            Add Funds
          </Button>
        </Card>

        {/* Transaction History */}
        <Card className="rounded-2xl shadow-md bg-white flex-1 overflow-hidden">
          <Title level={5}>Transaction History</Title>
          <List
            className="h-[40vh] overflow-y-auto"
            dataSource={history}
            renderItem={(item) => (
              <List.Item className="py-2 border-b last:border-b-0">
                <div className="flex flex-col w-full">
                  {/* First row: Type and Amount */}
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-sm">{item.type}</span>
                    <span className="text-sm">₹{item.amount}</span>
                  </div>

                  {/* Second row: Reason */}
                  {item.reason && (
                    <div className="mb-1">
                      <span className="text-gray-500 text-xs">
                        Reason: {item.reason}
                      </span>
                    </div>
                  )}

                  {/* Third row: Order ID and Date */}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">
                      {item.orderId}
                    </span>
                    <span
                      className={`font-bold text-xs ${
                        item.status === "SUCCESS"
                          ? "text-green-500"
                          : item.status === "PENDING"
                          ? "text-yellow-500"
                          : "text-red-500"
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Card>
      </div>

      {/* Payment Modal */}
      <Modal
        title="Add Funds to Wallet"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handlePaymentSubmit}>
            Send Payment Details
          </Button>,
        ]}
        width={400}
      >
        <Form form={form} layout="vertical">
          {/* Amount Display */}
          <div className="text-center mb-4">
            <Text strong className="text-lg">
              Amount: ₹{amount}
            </Text>
          </div>

          {/* Payment Method Selection */}
          <Form.Item label="Payment Method">
            <Select
              value={paymentMethod}
              onChange={setPaymentMethod}
              className="w-full"
            >
              <Option value="upi">UPI Payment</Option>
              <Option value="bank">Bank Transfer</Option>
            </Select>
          </Form.Item>

          {/* Payment Details */}
          {paymentMethod === "upi" ? (
            <div className="text-center mb-4">
              <div className="flex justify-center mb-2">
                <img
                  src={qr}
                  alt="QR Code"
                  className="w-[80PCX] h-[60vh] border rounded-lg"
                />
              </div>
              <Text strong>Scan QR Code to Pay</Text>
              <div className="mt-2 p-2 bg-gray-50 rounded">
                <div className="flex items-center justify-between">
                  <Text code className="text-sm">
                    UPI ID: {BANK_DETAILS.upi.id}
                  </Text>
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />} 
                    size="small"
                    onClick={copyUpiId}
                  >
                    Copy
                  </Button>
                </div>
                <Text className="text-xs text-gray-600">
                  Name: {BANK_DETAILS.upi.name}
                </Text>
              </div>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <div className="flex justify-between items-center mb-2">
                <Text strong>Bank Transfer Details</Text>
                <Button 
                  type="text" 
                  icon={<CopyOutlined />} 
                  size="small"
                  onClick={copyBankDetails}
                >
                  Copy All
                </Button>
              </div>
              <div className="mt-2 space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Bank: </Text>
                    <Text>{BANK_DETAILS.bank.name}</Text>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Account Number: </Text>
                    <Text>{BANK_DETAILS.bank.accountNumber}</Text>
                  </div>
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />} 
                    size="small"
                    onClick={copyAccountNumber}
                  >
                    Copy
                  </Button>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>IFSC: </Text>
                    <Text>{BANK_DETAILS.bank.ifsc}</Text>
                  </div>
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />} 
                    size="small"
                    onClick={copyIfsc}
                  >
                    Copy
                  </Button>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Account Name: </Text>
                    <Text>{BANK_DETAILS.bank.accountName}</Text>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transaction ID Input */}
          <Form.Item
            label="Transaction ID / UTR Number"
            required
            rules={[
              {
                required: true,
                message: "Please enter Transaction ID",
              },
            ]}
          >
            <Input
              placeholder="Enter transaction ID after payment"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              size="large"
            />
          </Form.Item>

          <div className="text-xs text-gray-500 text-center">
            After making the payment, enter the Transaction ID/UTR Number and click "Send Payment Details" to notify admin via WhatsApp.
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Wallet;



 